import { handleScrollToSelection } from './tiptap-caret-scroll'
import { AnyExtension } from '@tiptap/core'
import { Editor } from '@tiptap/vue-2'
import { markRaw } from 'vue'
import { buildCoreEditorExtensions } from './tiptap-editor-extensions-core'
import {
  ensureInteractiveEditorExtensions,
  getInteractiveEditorExtensionsModule,
  kickInteractiveEditorExtensionsRetry,
  prefetchInteractiveEditorExtensionsWhenEager
} from './tiptap-editor-interactive-loader'
import { registerEditorView, unregisterEditorView } from './tiptap-editor-view-registry'
import { trackEditor } from './tiptap-editor-diagnostics'
import type { CreateEditorParams } from './tiptap-editor-params'
import { editorContextFrom } from './tiptap-editor-params'

export type { CreateEditorParams } from './tiptap-editor-params'
export { editorContextFrom } from './tiptap-editor-params'
export {
  prefetchInteractiveEditorExtensions,
  ensureInteractiveEditorExtensions
} from './tiptap-editor-interactive-loader'

prefetchInteractiveEditorExtensionsWhenEager()

/**
 * Bump when an extension change alters the schema or its HTML output, so static renders cached under the old
 * schema are never served.
 */
export const SCHEMA_VERSION = 7

export async function createEditorAsync(params: CreateEditorParams): Promise<Editor> {
  await ensureInteractiveEditorExtensions()
  return createEditor(params)
}

export function createEditor(params: CreateEditorParams): Editor {
  // Synchronous callers cannot wait for the chunk: build from the core extensions (content and schema intact) and
  // start or retry the load, so editors created later get mentions, slash commands and variable autocomplete
  if (!getInteractiveEditorExtensionsModule()) kickInteractiveEditorExtensionsRetry()
  const createStart = performance.now()
  const { content, parent } = params
  const ctx = params.ctx || editorContextFrom(params)
  const extensions = buildEditorExtensions({ ...params, ctx })

  const editor = new Editor({
    extensions,
    editorProps: {
      handleKeyDown: (view, event) => (ctx.handleKeyDown ? ctx.handleKeyDown(view, event, extensions) : false),
      handleScrollToSelection
    },
    content: content
  })
  skipDeepObservation(editor)
  const { view } = editor
  registerEditorView(view)
  editor.on('destroy', () => unregisterEditorView(view))
  trackEditor(editor, params.diagnosticsKind || editorKind(parent), performance.now() - createStart)
  return editor
}

/** Schema and keymaps only; safe before the interactive chunk loads. */
export function buildSchemaEditorExtensions(params: CreateEditorParams): AnyExtension[] {
  return buildCoreEditorExtensions(params)
}

/**
 * The extension list of an editor created with these params; also yields the schema for static rendering. The
 * schema does not depend on the host: mentions and slash commands are always there, turned on by the context.
 */
export function buildEditorExtensions(params: CreateEditorParams): AnyExtension[] {
  const core = buildCoreEditorExtensions(params)
  const interactive = getInteractiveEditorExtensionsModule()
  if (!interactive) return core
  return interactive.mergeEditorExtensions(core, params)
}

/**
 * Components keep editors in reactive fields, so Vue 2 deep-walks each one on assignment,
 * which costs more than creating the editor. Mark the large, never-replaced internals raw.
 * `editor.view.state` stays reactive, so templates reading isActive/isEmpty still re-render.
 */
function skipDeepObservation(editor: Editor) {
  const { schema } = editor
  markRaw(schema)
  Object.values(schema.nodes).forEach(type => markRaw(type))
  Object.values(schema.marks).forEach(type => markRaw(type))
  markRaw(editor.extensionManager)
  markRaw(editor.options.extensions)
  editor.state.plugins.forEach(plugin => markRaw(plugin))
}

function editorKind(parent: Vue): string {
  return parent?.$vnode?.componentOptions?.tag || parent?.$options?.name || 'unknown'
}
